import { Button, Icon, ProgressBar, useToast } from '@blend-ed/blendx-ui';
import { getConfig } from '@edx/frontend-platform';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate } from 'react-router';
import BaseCard from './BaseCard';
import { CARD_TYPES, CARD_VARIANTS } from './constants';
import messages from './messages';

const LearnerCard = ({
  intl,
  title,
  link,
  homeLink,
  startDate,
  image,
  estimatedTime,
  totalCourses,
  price,
  type,
  progress,
  className,
  variant,
  onClick,
  redirect,
  currency,
}) => {
  const showToast = useToast();
  const navigate = useNavigate();

  const handleCopyLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(link);
    showToast('check', 'success', intl.formatMessage(messages.linkCopiedToClipboard));
  };

  const renderInfoPanel = () => {
    if (!estimatedTime && !totalCourses) return null;

    return (
      <div className="course-card__info-panel-items">
        {estimatedTime && (
          <div className="course-card__est-time">
            <Icon icon="time" type="line" size="xxs" className="course-card__est-time-icon" />
            <span>{estimatedTime}</span>
          </div>
        )}
        {totalCourses && (
          <div className="course-card__est-time">
            <Icon icon="book" type="line" size="xxs" className="course-card__est-time-icon" />
            <span>{totalCourses}</span>
          </div>
        )}
      </div>
    );
  };

  const renderFooter = () => {
    switch (type) {
      case CARD_TYPES.PROGRESS:
        return <ProgressBar className="course-card__footer-progressbar" progress={progress} type="inline" />;
      case CARD_TYPES.PRICE:
        return (
          <>
            <div className="course-card__price">
              {price === 0 ? intl.formatMessage(messages.free) : `${currency} ${price}`}
            </div>
            <Icon
              icon="share-forward"
              variant="ghost-gray"
              type="line"
              size="sm"
              className="course-card__share"
              onClick={handleCopyLink}
            />
          </>
        );
      case CARD_TYPES.LEARNING:
        return (
          <Button
            size="xs"
            variant="outline-gray"
            iconBefore="play"
            iconType="fill"
            border
            className="course-card__resume-button"
            onClick={async (e) => {
              e.stopPropagation();
              await onClick();
              if (!redirect) {
                navigate(link);
              } else {
                window.location.href = link;
              }
            }}
          >
            {intl.formatMessage(messages.resume)}
          </Button>
        );
      case CARD_TYPES.UPCOMING:
        return (
          <div className="course-card__start-date">
            {intl.formatMessage(messages.startDate, { startDate })}
          </div>
        );
      default:
        return null;
    }
  };

  const extraBadgeText = () => {
    if (startDate && new Date(startDate) < new Date()) {
      return {
        text: intl.formatMessage(messages.ongoing),
        variant: 'light-success',
      };
    } else if (startDate && new Date(startDate) > new Date()) {
      return {
        text: intl.formatMessage(messages.upcoming),
        variant: 'light-warning',
      };
    }
    return null;
  };

  return (
    <BaseCard
      title={title}
      link={link}
      image={new URL(image, getConfig().LMS_BASE_URL).href}
      estimatedTime={estimatedTime}
      className={classNames('course-card', className, { 'course-card--upcoming': type === CARD_TYPES.UPCOMING })}
      onClick={async () => {
        await onClick();
        if (!redirect) {
          navigate(type === CARD_TYPES.LEARNING ? homeLink : link);
        } else {
          window.location.href = type === CARD_TYPES.LEARNING ? homeLink : link;
        }
      }}
      renderFooter={renderFooter}
      renderInfoPanel={renderInfoPanel}
      badgeVariant={variant === CARD_VARIANTS.COURSE ? "light-yellow" : "light-success"}
      badgeText={variant}
      extraBadgeText={extraBadgeText()}
    />
  );
};

LearnerCard.propTypes = {
  intl: intlShape.isRequired,
  title: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  homeLink: PropTypes.string,
  startDate: PropTypes.string,
  image: PropTypes.string.isRequired,
  estimatedTime: PropTypes.string,
  totalCourses: PropTypes.number,
  price: PropTypes.number,
  type: PropTypes.string,
  progress: PropTypes.number,
  className: PropTypes.string,
  variant: PropTypes.string,
  onClick: PropTypes.func,
  redirect: PropTypes.bool,
  currency: PropTypes.string,
};

LearnerCard.defaultProps = {
  estimatedTime: null,
  totalCourses: null,
  price: null,
  type: null,
  progress: null,
  className: null,
  variant: null,
  onClick: () => { },
  redirect: false,
  currency: null,
  badgeVariant: null,
  badgeText: null,
};

export default injectIntl(LearnerCard); 